import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Parser } from 'html-to-react'

// Components
import { Modal } from '../Modal'
import { ThumbsUpIcon } from '../ThumbsUpIcon'

// Utils
import { replaceMergeFields } from './helpers'
import { validateLogic } from '../../utils/helpers'

/**
 *
 * WelcomePopup
 *
 */
const WelcomePopup = ({
  admissionItems,
  attendee,
  categories,
  config,
  completeCheckin,
  customPopups,
  close,
  externalPopupField,
  oilTypeOptions,
  sessions,
  staffMode,
}) => {
  const customPopupsToRender = customPopups.filter(
    (popup) =>
      validateLogic(popup.popupMessageLogic, attendee, {
        categories,
        admissionItems,
        sessions,
        oilTypes: oilTypeOptions,
      }) &&
      (popup.audience === 'Both' ||
        (popup.audience === 'Attendee' && !staffMode) ||
        (popup.audience === 'Staff' && staffMode)),
  )

  // When custom popup messages are displayed, staff confirm before the badge prints so they can
  // redirect the attendee to another station without wasting badge stock.
  const requiresConfirmation = customPopupsToRender.length > 0

  // State
  const [printed, setPrinted] = useState(false)

  // Without custom popup messages, check the attendee in and print immediately, then auto close
  // after 10 seconds.
  useEffect(() => {
    if (requiresConfirmation) return undefined

    completeCheckin()

    const timeout = setTimeout(() => close(), 10000) // 10 seconds

    return () => clearTimeout(timeout)
  }, [])

  // Once the print is confirmed, leave the popup up so staff can finish reading it, then auto
  // close after 7 seconds.
  useEffect(() => {
    if (!printed) return undefined

    const timeout = setTimeout(() => close(), 7000) // 7 seconds

    return () => clearTimeout(timeout)
  }, [printed])

  /**
   * Checks the attendee in and prints their badge. Guarded so a double tap can't print twice or
   * write a second badge print log.
   */
  const handlePrint = () => {
    if (printed) return

    setPrinted(true)
    completeCheckin()
  }

  const getActions = () => {
    if (!requiresConfirmation) {
      return [{ type: 'cancel', label: 'Close', onClick: close }]
    }

    return [
      { type: 'cancel', label: printed ? 'Close' : 'Cancel', onClick: close },
      {
        type: 'submit',
        label: printed ? 'Badge printed' : 'Print Badge',
        disabled: printed,
        onClick: handlePrint,
      },
    ]
  }

  return (
    <Modal
      actions={getActions()}
      content={
        <div className="mt-4 flex flex-col items-center">
          <span className="text-center text-2xl font-bold">
            {replaceMergeFields(attendee, config.header)}
          </span>
          <p className="mt-4 text-center text-gray">
            {Parser().parse(replaceMergeFields(attendee, config.body))}
          </p>

          {externalPopupField && attendee.customData[externalPopupField] && (
            <p className="mt-4 text-center text-gray">{attendee.customData[externalPopupField]}</p>
          )}

          {staffMode && attendee.customNotes && (
            <p className="mt-4 text-center text-gray">{attendee.customNotes}</p>
          )}

          <ul className={customPopupsToRender.length > 1 && 'list-disc'}>
            {customPopupsToRender.map((popup) => (
              <li key={popup.id} className="mt-4 text-center text-gray">
                {Parser().parse(replaceMergeFields(attendee, popup.message))}
              </li>
            ))}
          </ul>
        </div>
      }
      icon={<ThumbsUpIcon className="h-6 w-6 fill-white" />}
      open
    />
  )
}

WelcomePopup.defaultProps = {
  admissionItems: null,
  categories: null,
  externalPopupField: null,
  oilTypeOptions: null,
  sessions: null,
  staffMode: false,
}

WelcomePopup.propTypes = {
  admissionItems: PropTypes.object,
  attendee: PropTypes.object.isRequired,
  categories: PropTypes.object,
  completeCheckin: PropTypes.func.isRequired,
  config: PropTypes.object.isRequired,
  close: PropTypes.func.isRequired,
  externalPopupField: PropTypes.string,
  oilTypeOptions: PropTypes.object,
  sessions: PropTypes.object,
  staffMode: PropTypes.bool,
  customPopups: PropTypes.array.isRequired,
}

export { WelcomePopup }
